import 'dayjs/locale/sv';

import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { ArrowLeftCircle } from 'react-feather';

import { Spinner } from '@/components/spinner';
import { Typography } from '@/components/typography';
import { useTranslation } from 'react-i18next';
import { useCampaign } from '@/hooks/client';
import { TranslatedLink } from '@/utils/translatedComponents';

import useIsIframe from '@/hooks/useIsIframe';
import { useAppContext } from '@/context';
import { Link } from '../link';
import styles from './Wrapper.module.css';

interface WrapperProps {
  children: React.ReactNode;
  description?: string;
  goBackUrl?: string;
  heading?: React.ReactNode;
  isLoading?: boolean;
  hideFooter?: boolean;
  campaignId?: string;
}

export function Wrapper({
  children,
  description = '',
  goBackUrl = '',
  heading,
  isLoading = false,
  hideFooter = false,
  campaignId
}: WrapperProps) {
  const router = useRouter();
  const { t } = useTranslation();
  const isIframe = useIsIframe();
  const { campaign, setCampaign } = useAppContext();

  const { data: campaignData = {} } = useCampaign(campaignId, !!campaignId);
  const memoCampaign = useMemo(() => campaignData, [campaignData]);

  useEffect(() => {
    // eslint-disable-next-line
    const isValidPath = /^\/connect\/campaigns\/[^\/]+(\/[^\/]+)?$/.test(router.asPath);

    if (isValidPath && campaignData) {
      if (!campaign || campaign.campaign_id !== campaignData.campaign_id) {
        setCampaign(campaignData);
      }
    }
  }, [router.asPath, campaignData, campaign, setCampaign]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.wrapperBody}>
        {memoCampaign.is_demo && (
          <div className={styles.wrapperDemo}>{t('translatedType.DEMO')}</div>
        )}

        {heading && (
          <div className={styles.wrapperHeading}>
            {goBackUrl && (
              <div className={styles.wrapperHeadingGoBack} data-testid="goBackUrlLink">
                <TranslatedLink href={goBackUrl}>
                  <ArrowLeftCircle size={32} />
                </TranslatedLink>
              </div>
            )}

            <div className={styles.wrapperHeadingContent}>
              {typeof heading === 'string' ? (
                <Typography sizes={['h2']}>{heading}</Typography>
              ) : (
                heading
              )}
            </div>
          </div>
        )}

        {description && (
          <div className={styles.wrapperDescription}>
            <Typography sizes={['body']}>{description}</Typography>
          </div>
        )}

        {isLoading ? (
          <div className={styles.wrapperLoader} data-testid="spinner">
            <Spinner />
          </div>
        ) : (
          children
        )}
      </div>

      {!isIframe && !hideFooter && !isLoading && (
        <div className={styles.wrapperFooter}>
          &copy;
          {` ${dayjs().year()} Beatly. ${t('footer.copyright')}`}
          <Link href="https://www.beatly.com/latest-product-updates" target="_blank">
            {t('footer.productUpdates')}
          </Link>
          <Link href="https://www.beatly.com/info/privacy-pollicy" target="_blank">
            {t('footer.privacyPolicy')}
          </Link>
          <Link href="https://www.beatly.com/info/terms-of-service" target="_blank">
            {t('footer.termsOfService')}
          </Link>
          <Link href="https://www.beatly.com/info/cookie-pollicy" target="_blank">
            {t('footer.cookieSettings')}
          </Link>
        </div>
      )}
    </div>
  );
}
